'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Taxonomy service.
 * Uses Q-based promises for all operations.
 */

(function($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new TaxonomyService object.
   *
   * Argument 'taxonomyServiceUrl' - The URL to the taxonomy service in the ACE API.
   * Argument 'serviceSettings' - An object containing settings for this service.
   *
   * The 'serviceSettings' object should look like the following:
   *
   * {
   *   customQ: customQObject
   * }
   *
   * Property 'customQ' is an implementation of a promise based Q library,
   * i.e Angular $q. Defaults to a global Q.
   *
   * Returns a new TaxonomyService object.
   */
  atex.aceui.service.TaxonomyService = function(taxonomyServiceUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Returns a list of all taxonomies available in the system.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved with an object like:
       *
       * [
       *   {
       *     id: 'taxonomy',
       *     name: 'Taxonomy'
       *   }
       * ]
       *
       * Property 'id' of an object in the list is the taxonomy alias of the taxonomy.
       * Property 'name' of an object in the list is the name of the taxonomy.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      listTaxonomies: function(transferInfo) {
        var deferred = q.defer();

        $.ajax(taxonomyServiceUrl + '/structure?format=json', {
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (transferInfo.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", transferInfo.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            deferred.resolve (data);
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.TaxonomyService', {
              401: 'Could not authenticate user while getting taxonomy list from the taxonomy service!',
              403: 'Permission denied when getting structure taxonomy list from the taxonomy service!',
                   'default': 'There was an error while getting structure taxonomy list from the taxonomy service!'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.transferInfo = transferInfo;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Retrieves information for the categorization object represented by the transferInfo object.
       * The categorization object may represent a taxonomy, dimension or an entity.
       *
       * Argument 'transferInfo' - An object containing information on the categorization object
       * to read. The object should look like the following:
       *
       * {
       *   id: 'TAXONOMY ID|DIMENSION ID|ENTITY ID',
       *   depth: NUMBER,
       *
       *   authToken: 'TOKEN'
       * }
       *
       * Property 'id' is the ID of the categorization object to read.
       *
       * Property 'depth' is the restricted number of structure levels to return. If not specified,
       * the full taxonomy structure from the given structure point will be returned.
       *
       * Property 'authToken' is the authentication token to use when reading the categorization object.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved with an object that conforms
       * to the Atex Taxonomy format: http://support.polopoly.com/confluence/display/PolopolyMaster/Web+Service+Reference
       *
       * For example, if retrieving a taxonomy, the object will look like this:
       *
       * {
       *   id: 'TAXONOMYID',
       *
       *   data: {
       *     id: 'TAXONOMYID',
       *     name: 'TAXONOMY NAME',
       *
       *     dimensions: [
       *       {
       *         id: 'DIMENSIONID',
       *         name: 'DIMENSION NAME',
       *
       *         entities: [...],
       *         enumerable: BOOLEAN
       *       }
       *     ]
       *   }
       * }
       *
       * Property 'id' is the ID of the read categorization object.
       *
       * Property 'data' is the data as returned from the Taxonomy service, according to what you
       * requested (i.e. if you requested a taxonomy, the data object will contain information specific
       * to a taxonomy). See the previously mentioned documentation on the Atex Taxonomy format.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      getStructure: function(transferInfo) {
        var deferred = q.defer();

        var fullUrl = taxonomyServiceUrl + '/structure/' + transferInfo.id + '?format=json';

        if (typeof transferInfo.depth !== 'undefined') {
          fullUrl = fullUrl + '&depth=' + transferInfo.depth;
        }

        $.ajax(fullUrl, {
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (transferInfo.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", transferInfo.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            deferred.resolve( data );
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.TaxonomyService', {
              401: 'Could not authenticate user while getting structure "' + transferInfo.id + '" from the taxonomy service!',
              403: 'Permission denied when getting structure "' + transferInfo.id + '" from the taxonomy service!',
                   'default': 'There was an error while getting structure "' + transferInfo.id + '" from the taxonomy service!'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;

              e.rootCause.transferInfo = transferInfo;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Retrieves a list of suggested entities for the input string contained in the transferInfo object.
       *
       * Argument 'transferInfo' - An object containing information on what to retrieve suggestions for.
       * The object should look like the following:
       *
       * {
       *   dimensionId: 'DIMENSIONID',
       *   entityString: 'SOME STRING',
       *
       *   authToken: 'TOKEN'
       * }
       *
       * Property 'dimensionId' is the ID of the dimension within which we want to retrieve suggestions.
       * Property 'entityString' is the string to retrieve suggestions for.
       * Property 'authToken' is the authentication token to use when retrieving suggestions.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved with an object like:
       *
       * {
       *   dimension: {
       *     id: 'DIMENSION_ID',
       *     name: 'DIMENSION_NAME'
       *   },
       *   completions: [{
       *     id: 'ENTITY_A_ID',
       *     name: 'ENTITY_A_NAME,
       *     namePath: 'ENTITY_A_PATH_BY_NAME'
       *   }, {
       *     id: 'ENTITY_B_ID',
       *     name: 'ENTITY_B_NAME,
       *     namePath: 'ENTITY_B_PATH_BY_NAME'
       *   }]
       * }
       *
       * Property 'id' is the ID of the dimension within which we wanted suggestions.
       * Property 'name' is the (localized) name of the dimension.
       *
       * Property 'completions' is a list of completion objects.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      getSuggestions: function(transferInfo) {
        var deferred = q.defer();

        var fullUrl = taxonomyServiceUrl + '/complete/' + transferInfo.dimensionId + '/'
          + encodeURIComponent(transferInfo.entityString).replace(/%2[fF]/g, '/') + '?format=json';

        $.ajax(fullUrl, {
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (transferInfo.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", transferInfo.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            deferred.resolve( data );
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.TaxonomyService', {
              401: 'Could not authenticate user while getting suggestions from the server',
              403: 'Permission denied when getting suggestions from the server',
              'default': 'There was an error while getting suggestions from the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;

              e.rootCause.transferInfo = transferInfo;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      }
    };
  };
})(jQuery);
